import { useEffect, useRef, useState } from "react";

/**
 * Small "i" icon that opens a text bubble explaining the field next to it.
 * Click/tap to open (not hover-only — managers use this on tablets too).
 * Closes on outside click, Escape, or a second tap. Bubble flips above/below
 * depending on available space so it never runs off the top of the viewport.
 */
export function InfoTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [above, setAbove] = useState(true);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const el = wrapRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setAbove(r.top > 120); // flip below if too close to the top
    }
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="info-tip" ref={wrapRef}>
      <button
        type="button"
        className="info-tip-btn"
        aria-label={`What is this field? ${text}`}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open && (
        <span className={`info-tip-bubble ${above ? "above" : "below"}`} role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}
